import { useCallback, useLayoutEffect, useRef } from 'react';
import { formatUnitValue, toCanonical } from './unitConversions';

const initialCanonical = Symbol('initialCanonical');
const getAt = (object, path) => path.split('.').reduce((value, key) => value?.[key], object);
const setAt = (object, path, value) => {
  const keys = path.split('.');
  let target = object;
  for (const key of keys.slice(0, -1)) {
    target[key] = { ...target[key] };
    target = target[key];
  }
  target[keys[keys.length - 1]] = value;
};

// Canonical snapshots retain exact values when an inch input is rounded for readability.
export function useUnitDrafts(state, setState, fields, unitSystem) {
  const priorSystem = useRef(unitSystem);
  const canonical = useRef(new Map());
  if (canonical.current.size === 0 && state[initialCanonical]) {
    for (const [path, metric] of Object.entries(state[initialCanonical])) {
      canonical.current.set(path, { text: String(getAt(state, path)), metric });
    }
  }
  const getCanonical = useCallback((path, raw, kind) => {
    const entry = canonical.current.get(path);
    return entry && entry.text === String(raw) ? entry.metric : toCanonical(raw, kind, unitSystem);
  }, [unitSystem]);
  useLayoutEffect(() => {
    if (priorSystem.current !== unitSystem) {
      const oldSystem = priorSystem.current;
      priorSystem.current = unitSystem;
      setState(current => {
        let next = current;
        for (const [path, kind] of Object.entries(fields)) {
          const raw = getAt(current, path);
          if (raw === '' || raw === null || raw === undefined || !Number.isFinite(Number(raw))) continue;
          const entry = canonical.current.get(path);
          const metric = entry && entry.text === String(raw) ? entry.metric : toCanonical(raw, kind, oldSystem);
          const text = formatUnitValue(metric, kind, unitSystem, { input: true });
          canonical.current.set(path, { text, metric });
          if (next === current) next = { ...current };
          setAt(next, path, text);
        }
        return next;
      });
    } else {
      for (const path of canonical.current.keys()) if (!(path in fields)) canonical.current.delete(path);
      for (const [path, kind] of Object.entries(fields)) {
        const raw = getAt(state, path);
        if (raw === '' || raw === null || raw === undefined || !Number.isFinite(Number(raw))) canonical.current.delete(path);
        else {
          const entry = canonical.current.get(path);
          if (!entry || entry.text !== String(raw)) canonical.current.set(path, { text: String(raw), metric: toCanonical(raw, kind, unitSystem) });
        }
      }
    }
  }, [state, setState, fields, unitSystem]);
  return getCanonical;
}

export function initialUnitDrafts(initial, fields, unitSystem) {
  if (unitSystem === 'metric') return initial;
  const next = { ...initial };
  const canonical = {};
  for (const [path, kind] of Object.entries(fields)) {
    const raw = getAt(initial, path);
    if (raw !== '' && raw !== null && raw !== undefined && Number.isFinite(Number(raw))) {
      canonical[path] = Number(raw);
      setAt(next, path, formatUnitValue(raw, kind, unitSystem, { input: true }));
    }
  }
  Object.defineProperty(next, initialCanonical, { value: canonical });
  return next;
}
